import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import TimeField from '../../../components/atoms/forms/TimeField';
import FieldError from '../../../components/atoms/forms/FieldError';
import ReasonPicker from './ReasonPicker';
import { REMOVE_REASONS, isReasonComplete } from './reasons';
import {
  normalizeClockTo24h,
  snapToTimeSlot,
  parseTimeToMinutes,
} from './utils';

// Authors a single anchor entry for ResequenceReroute. Drag-drop and
// per-row Pin / window-cell click both open this modal — the visible copy
// branches on `intent`:
//   - 'window'     (default) — explicit pin: title/body talk about
//                              "anchoring a delivery window".
//   - 'resequence' — drag-drop: title/body talk about "moving the stop"
//                    and the supervisor confirms the move + window change
//                    in one step.
// The submitted shape is identical for both; Nextmv consumes the start/end
// window, not the drop position.

export const PIN_INTENT_WINDOW = 'window';
export const PIN_INTENT_RESEQUENCE = 'resequence';

const COPY_BY_INTENT = {
  [PIN_INTENT_WINDOW]: {
    title: 'Pin stop to time window',
    summary: (name) => (
      <>
        Anchor <strong>{name || 'this stop'}</strong> to a delivery window.
        Nextmv will resequence the rest of the route to honor it.
      </>
    ),
    submitLabel: 'Pin stop',
  },
  [PIN_INTENT_RESEQUENCE]: {
    title: 'Move stop in route',
    summary: (name) => (
      <>
        Reorder <strong>{name || 'this stop'}</strong> within this route. Its
        existing delivery window is pre-filled below — adjust it if the move
        also implies a different time.
      </>
    ),
    submitLabel: 'Confirm move',
  },
};

// Normalize a prefill clock string to a TimeField slot value: convert
// 12-hour / seconds-bearing input to "HH:MM", then snap to the nearest
// 15-minute slot. Falls through to empty when the input is unparseable so
// the select renders the placeholder.
function prepareSlot(value) {
  const normalized = normalizeClockTo24h(value);
  return normalized ? snapToTimeSlot(normalized) : '';
}

const StyledForm = styledComponent('form', {
  '& .psm-field': {
    marginBottom: theme.spacing.fieldGap,
  },
  '& .psm-label': {
    display: 'flex',
    flexDirection: 'column',
    fontSize: theme.typography.label.fontSize,
    fontWeight: theme.fontWeights.medium,
    color: theme.colors.text,
    marginBottom: theme.spacing.inputLabelGap,
  },
  '& .psm-label-text': {
    marginBottom: theme.spacing.inputLabelGap,
  },
  // Standalone label used when the input/select can't be nested inside a
  // <label> (react-select renders complex internals that don't pair with
  // htmlFor/inputId neatly).
  '& .psm-label-standalone': {
    display: 'block',
    fontSize: theme.typography.label.fontSize,
    fontWeight: theme.fontWeights.medium,
    color: theme.colors.text,
    marginBottom: theme.spacing.inputLabelGap,
  },
  '& .psm-window': {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing.fieldGap,
  },
  '& .psm-summary': {
    background: theme.colors.bgMuted,
    padding: theme.spacing.bodyPadding,
    borderRadius: theme.radii.input,
    marginBottom: theme.spacing.fieldGap,
    fontSize: theme.typography.label.fontSize,
    color: theme.colors.textDark,
  },
  '& .psm-next-day-hint': {
    fontSize: theme.typography.hint.fontSize,
    color: theme.colors.textMuted,
    marginTop: theme.spacing.labelGap,
  },
});

const WINDOW_MIN_MINUTES = 60;

// Minutes in the window, treating end <= start as ending on the next
// calendar day — cross-midnight windows are supported (13:00 – 01:00),
// and equal times mean a full 24-hour window. Null when either field is
// blank/malformed.
function windowSpanMinutes(start, end) {
  const startMin = parseTimeToMinutes(start);
  const endMin = parseTimeToMinutes(end);
  if (startMin === null || endMin === null) return null;
  return endMin <= startMin ? endMin + 1440 - startMin : endMin - startMin;
}

// Valid when both fields are filled and the span (possibly across
// midnight) is at least 60 minutes.
function isWindowValid(start, end) {
  const span = windowSpanMinutes(start, end);
  return span !== null && span >= WINDOW_MIN_MINUTES;
}

// Human-readable error when both fields are filled but the window is too
// short. '' while fields are being entered or when the window is valid.
function windowError(start, end) {
  const span = windowSpanMinutes(start, end);
  if (span === null) return '';
  if (span < WINDOW_MIN_MINUTES) {
    return 'Delivery window must be at least 1 hour.';
  }
  return '';
}

// Non-error cue that the chosen end time lands on the next calendar day.
function isNextDayWindow(start, end) {
  const startMin = parseTimeToMinutes(start);
  const endMin = parseTimeToMinutes(end);
  return startMin !== null && endMin !== null && endMin <= startMin;
}

function PinStopModal({ show, stopName, prefill, intent, onSubmit, onCancel }) {
  const [startTimeWindow, setStartTimeWindow] = useState('');
  const [endTimeWindow, setEndTimeWindow] = useState('');
  const [reasonValue, setReasonValue] = useState('');
  const [reasonNote, setReasonNote] = useState('');

  const prevShowRef = useRef(false);

  useEffect(() => {
    if (show && !prevShowRef.current) {
      setStartTimeWindow(prepareSlot(prefill?.startTimeWindow));
      setEndTimeWindow(prepareSlot(prefill?.endTimeWindow));
      setReasonValue('');
      setReasonNote('');
    }
    prevShowRef.current = show;
  }, [show, prefill]);

  const copy = COPY_BY_INTENT[intent] || COPY_BY_INTENT[PIN_INTENT_WINDOW];

  const windowErrorMessage = windowError(startTimeWindow, endTimeWindow);
  const showNextDayHint = isNextDayWindow(startTimeWindow, endTimeWindow);
  const canSubmit =
    isWindowValid(startTimeWindow, endTimeWindow) &&
    isReasonComplete({ value: reasonValue, note: reasonNote });

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      startTimeWindow,
      endTimeWindow,
      reason: { value: reasonValue, note: reasonNote },
    });
  };

  return (
    <Modal show={show} onHide={onCancel} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{copy.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <StyledForm onSubmit={handleSubmit} data-testid="pin-stop-form">
          <div className="psm-summary">{copy.summary(stopName)}</div>

          <div className="psm-field">
            <div className="psm-window">
              <div>
                <label className="psm-label">
                  <span className="psm-label-text">Start time</span>
                  <TimeField
                    value={startTimeWindow}
                    onChange={setStartTimeWindow}
                    includeEmpty
                    emptyLabel="—"
                    data-testid="pin-stop-start"
                  />
                </label>
              </div>
              <div>
                <label className="psm-label">
                  <span className="psm-label-text">End time</span>
                  <TimeField
                    value={endTimeWindow}
                    onChange={setEndTimeWindow}
                    includeEmpty
                    emptyLabel="—"
                    data-testid="pin-stop-end"
                  />
                </label>
              </div>
            </div>
            <FieldError
              id="pin-stop-window-error"
              message={windowErrorMessage}
              data-testid="pin-stop-window-error"
            />
            {showNextDayHint && (
              <div
                className="psm-next-day-hint"
                data-testid="pin-stop-next-day-hint"
              >
                End time is on the next day.
              </div>
            )}
          </div>

          <div className="psm-field">
            <span className="psm-label-standalone">Reason</span>
            <ReasonPicker
              options={REMOVE_REASONS}
              value={reasonValue}
              note={reasonNote}
              onChange={({ value, note }) => {
                setReasonValue(value);
                setReasonNote(note);
              }}
              reasonTestId="pin-stop-reason"
              noteTestId="pin-stop-note"
            />
          </div>
        </StyledForm>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onCancel} data-testid="pin-stop-cancel">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="btn-success"
          data-testid="pin-stop-submit"
        >
          {copy.submitLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

PinStopModal.propTypes = {
  show: PropTypes.bool.isRequired,
  stopName: PropTypes.string,
  prefill: PropTypes.shape({
    startTimeWindow: PropTypes.string,
    endTimeWindow: PropTypes.string,
  }),
  intent: PropTypes.oneOf([PIN_INTENT_WINDOW, PIN_INTENT_RESEQUENCE]),
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

PinStopModal.defaultProps = {
  stopName: '',
  prefill: null,
  intent: PIN_INTENT_WINDOW,
};

export default PinStopModal;
